/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import * as logger from '@ion/core/src/logger/logger';
import {
  formatReconnectionAttempts,
  formatSubscriptionResults
} from './helpers';
import {
  SocketConnection,
  SubscriptionResults
} from './types';

/**
 * Provides the logger for outputting a pre-handshake token retrieval success.
 */
export const preauthSucceeded = () => {
  log('Pre-Auth Single Use Token Successfully Retrieved');
};

/**
 * Provides the logger for outputting a pre-handshake token retrieval failure.
 */
export const preauthFailed = (err: Error) => {
  log(`Pre-Auth Single Use Token Retrieval Failed [${err?.toString()}]`, null, true);
};

/**
 * Provides the logger for outputting a connection event, which is to be
 * logged when a connection is established.
 */
export const connected = (event: Event, instance: SocketConnection) => {
  log('Connection Successfully Established', instance);
};

/**
 * Provides the logger for outputting a connection close event, which is to be
 * logged when a connection is closed (either explicitly or implicitly).
 */
export const closed = (event: CloseEvent | {reason: string}, instance: SocketConnection) => {
  log(`Connection Closed [${event?.reason || 'N/A'}]`, instance);
};

/**
 * Provides the logger for outputting the results of a subscription invocation,
 * whereby entity type, subscription results, and failures are logged.
 */
export const subscription = (results: SubscriptionResults, isSubscription: boolean, socket: SocketConnection) => {
  log(formatSubscriptionResults(results, isSubscription), socket);
};

/**
 * Provides the logger for outputting a data event, which is to be logged when a
 * message is received.
 */
export const data = (event: MessageEvent, instance: SocketConnection) => {
  log('Message Received', instance);
};

/**
 * Provides the logger for outputting a connection error event, which is to be
 * logged when a connection fails.
 */
export const error = (event: ErrorEvent | {message: string}, instance: SocketConnection) => {
  log(`Connection Failure: [${event.message}]`, instance, true);
};

/**
 * Provides the logger for outputting a connection error event, which is to be
 * logged when a connection fails.
 */
export const processingException = ({message}: Error, instance: SocketConnection) => {
  log(`Incoming Message Processing Failure: [${message}]`, instance, true);
};

/**
 * Provides the logger for outputting a reconnection attempt, which is to be
 * logged when a connection fails and a new connection is being established.
 */
export const reconnect = (attempts: number, instance: SocketConnection) => {
  log(`Attempting to Re-establish Connection ${formatReconnectionAttempts(attempts)} ...`, instance);
};

/**
 * Provides the logger for outputting a reconnection success, which is to be
 * logged when a reconnection attempt succeeds.
 */
export const reconnected = (attempts: number, instance: SocketConnection) => {
  log(`Connection Successfully Re-established after ${formatReconnectionAttempts(attempts, false)}`, instance);
};

/**
 * Provides the logger for outputting a keepalive ping message response.
 */
export const keepalive = (instance: SocketConnection) => {
  log('Keep Alive Ping Successfully Executed.', instance);
};

/**
 * Provides the default logging facility through which all Socket logs are generated
 * and emitted.
 *
 * @param msg      Defines the message to be logged to the underlying logger.
 * @param instance Defines the Socket instance for which the message is to be logged.
 * @param isError  When true, indicates that the error logger is to be used.
 */
const log = (msg: string, instance?: SocketConnection, isError = false) => {
  const status  = instance ? (instance?.isConnected ? 'CONNECTED' : 'DISCONNECTED') : null;
  const message = `[WebSocket API] ${msg}.${status ? ` Status: ${status}` : ''}`;
  logger[isError ? 'error' : 'info'](message);
};
